
import { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoginLayout from "layouts/login"
import StoreSettingService from "services/store_setting";
import PackageService from "services/package";
import AppLoader from "components/app-loader";

const service = new StoreSettingService();
const packageService = new PackageService();

const defaultSubscriptionStatus = {
    canCreateProduct: { canCreate: true, remaining: Infinity, limit: Infinity, current: 0 },
    canCreateOrder: { canCreate: true, remaining: Infinity, limit: Infinity, current: 0 },
    storageStatus: { usedGB: 0, limitGB: Infinity, percentUsed: 0, canUpload: true }
};

/**
 * Compute subscription status validators
 */
const computeSubscriptionStatus = (subscription) => {
    const limits = subscription?.package?.limits || subscription?.current?.limits;
    const usage = subscription?.usage || subscription?.current?.usage;

    if (!limits || !usage) {
        return defaultSubscriptionStatus;
    }
    
    // Product limit check
    const productLimit = Number(limits.product_count || 0);
    const productUsed = Number(usage.product_count || 0);
    const productRemaining = productLimit - productUsed;
    const canCreateProduct = {
        canCreate: productRemaining > 0,
        remaining: Math.max(0, productRemaining),
        limit: productLimit,
        current: productUsed
    };
    
    // Order limit check
    const orderLimit = Number(limits.order_count_month || 0);
    const orderUsed = Number(usage.order_count_month || 0);
    const orderRemaining = orderLimit - orderUsed;
    const canCreateOrder = {
        canCreate: orderRemaining > 0,
        remaining: Math.max(0, orderRemaining),
        limit: orderLimit,
        current: orderUsed
    };
    
    // Storage check
    const storageBytes = Number(usage.storage_used_bytes ?? usage.storage_bytes ?? 0);
    const usedGB = storageBytes / (1024 ** 3);
    const limitGB = Number(limits.storage_gb || 0);
    const percentUsed = limitGB > 0 ? (usedGB / limitGB) * 100 : 0;
    const storageStatus = {
        usedGB: parseFloat(usedGB.toFixed(2)),
        limitGB,
        percentUsed: parseFloat(percentUsed.toFixed(1)),
        canUpload: limitGB > 0 ? usedGB < limitGB : true
    };
    
    return {
        canCreateProduct,
        canCreateOrder,
        storageStatus
    };
};

const WorkspaceContext = createContext();

function WorkspaceProvider({ children }) {

    const [workspace, setWorkSpace] = useState({ 
        collaboratorSetting: {},
        subscriptionStatus: defaultSubscriptionStatus
    });


    useEffect(() => {
        loadData();

    }, [])

    const loadData = async () => {
        try {
            const [setting, subscriptionData] = await Promise.all([
                service.getCollaboratorSetting(),
                packageService.getCurrent()
            ]);
            
            const subscriptionStatus = computeSubscriptionStatus(subscriptionData);
            
            setWorkSpace({ 
                collaboratorSetting: setting,
                subscription: subscriptionData,
                subscriptionStatus
            })
        } catch (error) {
            console.error("Error loading workspace data:", error);
            // Set defaults on error
            setWorkSpace({ 
                collaboratorSetting: {},
                subscription: null,
                subscriptionStatus: defaultSubscriptionStatus
            })
        }
    }






    return <WorkspaceContext.Provider value={{ workspace }}>
        {children}

    </WorkspaceContext.Provider>;

}

const useWorkSpace = () => {
    const context = useContext(WorkspaceContext);
    return context;
};

export { useWorkSpace };
export default WorkspaceProvider;
